/**
 * Centralized LLM provider utilities.
 *
 * Single source of truth for:
 * - Provider detection from model name strings
 * - Provider brand logos (inline SVGs)
 * - Provider color definitions (hex, Tailwind classes)
 */

import { OpenAILogo, AnthropicLogo, GoogleGeminiLogo, DeepSeekLogo, GrokLogo, KimiLogo } from '@/components/icons/ProviderLogos';

export type LLMProvider = 'openai' | 'anthropic' | 'google' | 'deepseek' | 'grok' | 'kimi';

/** Detect provider from a model name string. */
export function getProviderFromModel(modelName: string): LLMProvider {
  const m = modelName.toLowerCase();
  if (m.includes('gpt') || m.includes('openai')) return 'openai';
  if (m.includes('claude') || m.includes('anthropic')) return 'anthropic';
  if (m.includes('deepseek')) return 'deepseek';
  if (m.includes('gemini') || m.includes('google')) return 'google';
  if (m.includes('grok') || m.includes('xai')) return 'grok';
  if (m.includes('kimi')) return 'kimi';
  return 'openai';
}

/** Get the brand logo component for a provider. */
export function getProviderLogo(provider: string, size: number = 16) {
  switch (provider) {
    case 'openai': return <OpenAILogo size={size} />;
    case 'anthropic': return <AnthropicLogo size={size} />;
    case 'google': return <GoogleGeminiLogo size={size} />;
    case 'deepseek': return <DeepSeekLogo size={size} />;
    case 'grok': return <GrokLogo size={size} />;
    case 'kimi': return <KimiLogo size={size} />;
    default: return <OpenAILogo size={size} />;
  }
}

/** Provider color definitions. */
export const PROVIDER_COLORS: Record<string, {
  hex: string;
  text: string;
  bg: string;
  border: string;
  gradient: string;
}> = {
  openai: {
    hex: '#10B981',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500',
    border: 'border-emerald-500/30',
    gradient: 'from-emerald-500/15 to-emerald-600/8 border-emerald-500/30',
  },
  anthropic: {
    hex: '#F97316',
    text: 'text-orange-400',
    bg: 'bg-orange-400',
    border: 'border-orange-500/30',
    gradient: 'from-orange-500/15 to-orange-600/8 border-orange-500/30',
  },
  google: {
    hex: '#3B82F6',
    text: 'text-blue-400',
    bg: 'bg-blue-400',
    border: 'border-blue-500/30',
    gradient: 'from-blue-500/15 to-blue-600/8 border-blue-500/30',
  },
  deepseek: {
    hex: '#8B5CF6',
    text: 'text-purple-400',
    bg: 'bg-purple-400',
    border: 'border-purple-500/30',
    gradient: 'from-purple-500/15 to-purple-600/8 border-purple-500/30',
  },
  grok: {
    hex: '#EF4444',
    text: 'text-red-400',
    bg: 'bg-red-400',
    border: 'border-red-500/30',
    gradient: 'from-red-500/15 to-red-600/8 border-red-500/30',
  },
  kimi: {
    hex: '#EC4899',
    text: 'text-pink-400',
    bg: 'bg-pink-400',
    border: 'border-pink-500/30',
    gradient: 'from-pink-500/15 to-pink-600/8 border-pink-500/30',
  },
};

const DEFAULT_COLORS = PROVIDER_COLORS.openai;

/** Get provider color in a specific format. */
export function getProviderColor(provider: string, variant: 'hex' | 'text' | 'bg' | 'border' | 'gradient' = 'text'): string {
  return (PROVIDER_COLORS[provider] || DEFAULT_COLORS)[variant];
}
